/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {ReportScheduleApiService, ReportScheduleModel} from './report-schedule-api.service';
import {ReportScheduleCopyModalComponent} from './report-schedule-copy-modal.component';
import {ReportScheduleEditPageComponent} from './report-schedule-edit-page.component';
import {ReportScheduleRoutePageComponent} from './report-schedule-route-page.component';

export function reportScheduleStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ReportScheduleApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageReportTemplates.apiKey)
    && !permissions.hasAny(PERMISSIONS.runReports.apiKey);
  const rh = adnRouterHelper.createRouterHelper('report-schedules.report-schedule', ReportScheduleApiService, 'app.rep', 'app');
  const copyConfig = {
    modalComponent: ReportScheduleCopyModalComponent,
    stateUrl: 'app.rep.report-schedules.report-schedule',
    lcase: 'report schedule',
    ucase: 'Report Schedule'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ReportScheduleRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      reportSchedulePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<ReportScheduleModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: [], recurrence: 'DAILY'},
            true, readOnly(), api, 'Report Schedule', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Report Schedule', 'name', copyConfig));
      },
      model: function(reportSchedulePage: SignalAdminResourcePageState<ReportScheduleModel>) {
        return reportSchedulePage.model();
      }
    },
    views: {
      'content@app': {component: ReportScheduleEditPageComponent, bindings: {state: 'reportSchedulePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'reportSchedulePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'reportSchedulePage'}}
    }
  }));
  return states.build();
}
